import { useEffect, useState } from "react";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, TrendingUp, Target, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  ahead: { label: "Ahead of pace", pill: "bg-emerald-100 text-emerald-700", bar: "bg-emerald-500" },
  exceeded: { label: "Target exceeded", pill: "bg-emerald-100 text-emerald-700", bar: "bg-emerald-500" },
  on_track: { label: "On track", pill: "bg-indigo-100 text-indigo-700", bar: "bg-indigo-500" },
  behind: { label: "Behind pace", pill: "bg-amber-100 text-amber-700", bar: "bg-amber-500" },
  no_target: { label: "No target set", pill: "bg-slate-100 text-slate-500", bar: "bg-slate-300" },
};

export default function MonthlyRevenue({ editable = false, className = "" }) {
  const [data, setData] = useState(null);     // null=loading, false=forbidden, object=loaded
  const [err, setErr] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setErr(false);
    api.get("/account/revenue")
      .then(({ data }) => { setData(data); setDraft(data.target != null ? String(data.target) : ""); })
      .catch((e) => { if (e.response?.status === 403) setData(false); else { setData(false); setErr(true); } });
  };
  useEffect(() => { load(); }, []);

  if (err) return (
    <div className={`rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between ${className}`} data-testid="monthly-revenue-error">
      <span className="text-sm text-slate-500">Couldn't load monthly revenue.</span>
      <Button size="sm" variant="outline" onClick={load} data-testid="mr-retry"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Retry</Button>
    </div>
  );
  if (!data) return null;

  const p = data.performance || {};
  const cur = data.currency || "USD";
  const st = STATUS[p.exceeded ? "exceeded" : (p.status || "no_target")] || STATUS.no_target;
  const configured = data.target_configured;
  const pct = p.pct_achieved;
  const bar = Math.min(100, Math.max(0, pct || 0));

  const saveTarget = async (clear = false) => {
    const val = clear ? null : (draft.trim() === "" ? null : Number(draft));
    if (val != null && (!Number.isFinite(val) || val < 0)) { toast.error("Enter a nonnegative amount."); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/account/revenue-target", { target: val });
      setData(data); setDraft(data.target != null ? String(data.target) : "");
      toast.success(clear || val == null ? "Target cleared." : "Monthly target saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save target."); }
    finally { setSaving(false); }
  };

  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 ${className}`} data-testid="monthly-revenue">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center w-8 h-8 rounded-lg bg-slate-900 text-white"><TrendingUp className="w-4 h-4" /></span>
          <div>
            <h3 className="font-heading font-semibold text-slate-900 leading-tight">Monthly revenue</h3>
            <p className="text-xs text-slate-500">{p.month_label} · {data.scope_label}</p>
          </div>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${st.pill}`} data-testid="mr-status">{st.label}</span>
      </div>

      <div className="mt-4 flex items-end gap-2 flex-wrap">
        <span className="text-3xl font-bold text-slate-900" data-testid="mr-actual">{fmtMoney(p.actual, cur)}</span>
        {configured
          ? <span className="text-sm font-medium text-slate-400 mb-1" data-testid="mr-target">/ {fmtMoney(p.target, cur)} target</span>
          : <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-2.5 py-1 mb-1" data-testid="mr-not-configured">Monthly target not configured</span>}
      </div>

      {configured && (
        <>
          <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-2 rounded-full ${st.bar}`} style={{ width: `${bar}%` }} />
          </div>
          <div className="mt-2 text-sm text-slate-600" data-testid="mr-pct">
            <span className="font-semibold text-slate-900">{pct != null ? `${Math.round(pct)}%` : "—"}</span> of target ·{" "}
            <span data-testid="mr-remaining">{(p.remaining || 0) > 0 ? `${fmtMoney(p.remaining, cur)} to go` : "target reached"}</span>
          </div>
          <p className="mt-1 text-xs text-slate-500" data-testid="mr-pace">
            {fmtMoney(Math.abs(p.pace_delta || 0), cur)} {(p.pace_delta || 0) >= 0 ? "ahead of" : "behind"} the {fmtMoney(p.expected, cur)} expected by today
          </p>
          {(p.required_daily || 0) > 0 && (
            <p className="mt-1 text-xs text-slate-500" data-testid="mr-required">
              Needs ~{fmtMoney(p.required_daily, cur)}/day for the remaining {p.days_remaining_incl_today} day(s)
            </p>
          )}
        </>
      )}
      <p className="mt-3 text-[11px] text-slate-400">Verified payments received this month, net of refunds — not a forecast.</p>

      {editable && (
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="mr-target-editor">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-2">
            <Target className="w-3.5 h-3.5" /> {data.scope === "platform" ? "Platform-wide monthly target" : "Your monthly target"}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="number" min="0" step="100" placeholder="e.g. 50000" value={draft}
              onChange={(e) => setDraft(e.target.value)} className="w-40" data-testid="mr-target-input" />
            <Button onClick={() => saveTarget(false)} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="mr-target-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save target"}
            </Button>
            {configured && <Button variant="ghost" onClick={() => saveTarget(true)} disabled={saving} data-testid="mr-target-clear">Clear</Button>}
          </div>
          <p className="mt-1.5 text-[11px] text-slate-400">
            {data.scope === "platform" ? "Applies platform-wide (admins only)." : "Only you can see and set this."} Pacing: behind &lt;90%, on track 90–110%, ahead &gt;110% of the expected pace.
          </p>
        </div>
      )}
    </div>
  );
}
